import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "@/components/app/Layout";
import { api } from "@/lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import MasonryGrid from "@/components/app/MasonryGrid";
import HireMeButton from "@/components/app/HireMeButton";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { MapPin, Globe, Mail, Bookmark } from "lucide-react";

export default function Profile() {
  const { username } = useParams();
  const handle = (username || "").replace(/^@/, "");
  const [profile, setProfile] = useState(null);
  const [projects, setProjects] = useState([]);
  const [collections, setCollections] = useState([]);
  const { user: me } = useAuth();

  const load = async () => {
    const [p, pr, co] = await Promise.all([
      api.get(`/users/${handle}`),
      api.get(`/projects`, { params: { username: handle, limit: 48 } }),
      api.get(`/users/${handle}/collections`).catch(() => ({ data: [] })),
    ]);
    setProfile(p.data); setProjects(pr.data); setCollections(co.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [handle]);

  const toggleFollow = async () => {
    if (!me) return toast.error("Login to follow");
    const { data } = await api.post(`/users/${handle}/follow`);
    setProfile({ ...profile, followed_by_me: data.followed, followers_count: data.followers_count });
  };

  if (!profile) return <Layout><div className="py-24 text-center text-muted-foreground">Loading…</div></Layout>;

  const isMe = me?.user_id === profile.user_id;

  return (
    <Layout>
      <div className="relative h-56 sm:h-72 bg-muted overflow-hidden">
        {profile.banner_url && <img src={profile.banner_url} alt="banner" className="w-full h-full object-cover" />}
      </div>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="-mt-16 relative flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 pb-10 border-b border-border/60">
          <div className="flex items-end gap-5">
            <Avatar className="h-28 w-28 ring-4 ring-background">
              <AvatarImage src={profile.avatar_url} />
              <AvatarFallback>{profile.full_name?.[0]}</AvatarFallback>
            </Avatar>
            <div className="pb-2">
              <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight" data-testid="profile-name">{profile.full_name}</h1>
              <div className="text-sm text-muted-foreground">@{profile.username} · {profile.profession}</div>
              {!isMe && (
                <div className="mt-3">
                  <HireMeButton username={profile.username} creatorName={profile.full_name?.split(" ")[0] || profile.username} />
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {!isMe && (
              <Button
                onClick={toggleFollow}
                variant={profile.followed_by_me ? "outline" : "default"}
                className={profile.followed_by_me ? "" : "bg-[hsl(var(--accent))] hover:bg-[hsl(15_100%_55%)] text-white"}
                data-testid="follow-btn"
              >
                {profile.followed_by_me ? "Following" : "Follow"}
              </Button>
            )}
            {isMe && <Link to="/dashboard/settings"><Button variant="outline" data-testid="edit-profile-btn">Edit profile</Button></Link>}
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-10 py-10">
          <aside className="lg:col-span-3 space-y-6">
            {profile.bio && <p className="text-sm text-muted-foreground">{profile.bio}</p>}
            <div className="space-y-2 text-sm">
              {profile.location && <div className="flex items-center gap-2 text-muted-foreground"><MapPin className="h-4 w-4" />{profile.location}</div>}
              {profile.website && <a href={profile.website} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-[hsl(var(--accent))]"><Globe className="h-4 w-4" />{profile.website.replace(/^https?:\/\//, "")}</a>}
              <div className="flex items-center gap-2 text-muted-foreground"><Mail className="h-4 w-4" />{profile.email}</div>
            </div>
            <div>
              <div className="meta-label mb-2">Skills</div>
              <div className="flex flex-wrap gap-1.5">{profile.skills?.map((s) => <Badge key={s} variant="secondary">{s}</Badge>)}</div>
            </div>
            <div>
              <div className="meta-label mb-2">Software</div>
              <div className="flex flex-wrap gap-1.5">{profile.software?.map((s) => <Badge key={s} variant="outline">{s}</Badge>)}</div>
            </div>
            <div className="grid grid-cols-3 text-center pt-4 border-t border-border/60">
              <div><div className="font-display font-bold text-xl">{profile.projects_count}</div><div className="meta-label mt-1">Projects</div></div>
              <div><div className="font-display font-bold text-xl">{profile.followers_count}</div><div className="meta-label mt-1">Followers</div></div>
              <div><div className="font-display font-bold text-xl">{profile.following_count}</div><div className="meta-label mt-1">Following</div></div>
            </div>
          </aside>
          <div className="lg:col-span-9">
            <Tabs defaultValue="works">
              <TabsList>
                <TabsTrigger value="works" data-testid="profile-tab-works">Works ({profile.projects_count})</TabsTrigger>
                <TabsTrigger value="collections" data-testid="profile-tab-collections">Collections ({collections.length})</TabsTrigger>
              </TabsList>
              <TabsContent value="works" className="mt-6">
                <MasonryGrid projects={projects} empty={isMe ? "You haven't published anything yet." : "No published projects yet."} />
              </TabsContent>
              <TabsContent value="collections" className="mt-6">
                {collections.length === 0 && <div className="text-sm text-muted-foreground py-10 text-center">No public collections yet.</div>}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {collections.map((c) => (
                    <Link key={c.collection_id} to={`/collections/${c.collection_id}`} className="group block rounded-lg overflow-hidden border border-border/60 bg-card card-hover" data-testid={`profile-col-${c.collection_id}`}>
                      <div className="grid grid-cols-2 gap-0.5 aspect-[4/3] bg-muted">
                        {(c.preview_covers || []).slice(0, 4).map((u, i) => (
                          <img key={i} src={u} alt="" className="w-full h-full object-cover" />
                        ))}
                        {(c.preview_covers || []).length === 0 && (
                          <div className="col-span-2 grid place-items-center text-muted-foreground text-sm"><Bookmark className="h-6 w-6" /></div>
                        )}
                      </div>
                      <div className="p-4">
                        <div className="font-display font-semibold">{c.title}</div>
                        <div className="meta-label mt-1">{c.project_count} works</div>
                      </div>
                    </Link>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </section>
    </Layout>
  );
}
